import AgGridReactTable from "@components/ag-grid/AgGridReactTable";
import AgGridSkeleton from "@components/ag-grid/AgGridSkeleton";
import TableActionButton from "@components/buttons/TableActionButton";
import CopyButton from "@components/buttons/CopyButton";
import LightCustomButton from "@components/buttons/LightCustomButton";
import GlobalSearchInputField from "@components/inputs/GlobalSearchInputField";
import ToggleInput from "@components/inputs/ToggleInput";
import SlidingPanel from "@components/sliders/SlidingPanel";
import { cashierApis } from "@services/apis/Cashier";
import {
  AutoTradeCashier,
  AutoTradeCashierAction,
  AutoTradeCashierStatusFilter,
} from "@services/models/Cashier/AutoTradeCashiersModel";
import { UpiConfigFlag } from "@services/models/Cashier/UpiConfigModel";
import useIsMobile from "@utils/hooks/useIsMobile";
import { formatDisplayDate } from "@utils/safeDate";
import { ColDef } from "ag-grid-community";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import useSnackbarMessage from "../../utils/HandleSuccessErrorSnackbars";

const PAGE_LIMIT = 20;

const STATUS_OPTIONS: {
  value: AutoTradeCashierStatusFilter;
  label: string;
}[] = [
  { value: "all", label: "All" },
  { value: "active", label: "Active" },
  { value: "paused", label: "Paused" },
  { value: "suspended", label: "Suspended" },
  { value: "deleted", label: "Deleted" },
];

const parseGroupId = (value: string): number | undefined => {
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  const parsed = Number(trimmed);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : undefined;
};

const formatAmount = (value: unknown) => {
  if (value == null || value === "") return "—";
  const amount = Number(value);
  if (!Number.isFinite(amount)) return "—";
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

const dateLabel = (value: string | null | undefined) =>
  formatDisplayDate(
    value,
    undefined,
    {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    },
    "—",
  );

const dateTooltip = (value: string | null | undefined) =>
  formatDisplayDate(
    value,
    undefined,
    {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    },
    "",
  );

const tightColumn = (
  width: number,
  column: ColDef<AutoTradeCashier>,
): ColDef<AutoTradeCashier> => ({
  tooltipValueGetter: (params) => {
    const value = params.valueFormatted || params.value;
    if (value == null || value === "" || value === "—") return "";
    return String(value);
  },
  ...column,
  width,
  minWidth: width,
  maxWidth: width,
  flex: 0,
  suppressSizeToFit: true,
});

const TIGHT_GRID_DEFAULT = {
  sortable: true,
  filter: true,
  resizable: true,
  flex: 0,
  suppressSizeToFit: true,
  floatingFilter: true,
  headerClass: "ag-header-cell-custom",
  cellClass: "ag-cell-custom",
};

const textOrDash = (value: string | number | null | undefined) => {
  if (value == null) return "—";
  const text = String(value).trim();
  return text || "—";
};

const recordStatus = (cashier: AutoTradeCashier) => {
  const raw = String(cashier.autoTrade?.status ?? "").toLowerCase();
  if (
    raw === "active" ||
    raw === "paused" ||
    raw === "suspended" ||
    raw === "deleted"
  ) {
    return raw;
  }
  if (Number(cashier.autoTrade?.deleted) === 1) return "deleted";
  if (Number(cashier.autoTrade?.suspended) === 1) return "suspended";
  if (Number(cashier.autoTrade?.paused) === 1) return "paused";
  return "active";
};

const isSuspended = (cashier: AutoTradeCashier) =>
  recordStatus(cashier) === "suspended" ||
  Number(cashier.autoTrade?.suspended) === 1;

const isDeleted = (cashier: AutoTradeCashier) =>
  recordStatus(cashier) === "deleted" ||
  Number(cashier.autoTrade?.deleted) === 1;

const statusClassName = (status: string) => {
  if (status === "active") return "bg-bg-status-green text-text-status-green";
  if (status === "paused") return "bg-bg-status-yellow text-text-status-yellow";
  if (status === "suspended") return "bg-bg-status-red text-text-status-red";
  return "bg-bg-header text-text-subtle";
};

const cashierStateLabel = (state: number | null | undefined) => {
  if (state === 0) return "Deleted";
  if (state === 1) return "Active";
  if (state == null) return "—";
  return "Suspended";
};

const upiLabel = (cashier: AutoTradeCashier) => {
  const method = cashier.paymentMethod;
  if (!method) return "—";
  if (method.upiId) return method.upiId;
  if (method.upiAddress || method.upiHandle) {
    return `${method.upiAddress ?? ""}${method.upiHandle ?? ""}`;
  }
  return "—";
};

const merchantLabel = (cashier: AutoTradeCashier) => {
  const merchants = cashier.group?.merchants ?? [];
  if (!merchants.length) return "—";
  return merchants
    .map((merchant) => merchant.merchantName || String(merchant.merchantId))
    .join(", ");
};

const accountLabel = (cashier: AutoTradeCashier) => {
  const flags: string[] = [];
  if (Number(cashier.user?.isBlocked) === 1) flags.push("Blocked");
  if (Number(cashier.user?.isDebitFrozen) === 1) flags.push("Debit frozen");
  return flags.length ? flags.join(", ") : "Clear";
};

const rampLabel = (cashier: AutoTradeCashier) => {
  const access = cashier.accessAndLimits;
  if (!access) return "—";
  const parts = [
    Number(access.takerP2pRampFiat) === 1 ? "Fiat" : null,
    Number(access.takerP2pRampCrypto) === 1 ? "Crypto" : null,
  ].filter(Boolean);
  return parts.length ? parts.join(", ") : "None";
};

const getApiErrorMessage = (error: unknown, fallback: string) => {
  const err = error as {
    response?: { data?: { msg?: string } };
    message?: string;
  };
  return err?.response?.data?.msg || err?.message || fallback;
};

const groupLabel = (cashier: AutoTradeCashier) => {
  const group = cashier.group;
  if (!group) return "—";
  const name = group.groupName?.trim() || "Group";
  return group.groupId != null ? `${name} (${group.groupId})` : name;
};

const AutoTradeUpisManagement = () => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { handleError, handleSuccess } = useSnackbarMessage();
  const handleErrorRef = useRef(handleError);
  handleErrorRef.current = handleError;
  const gridRef = useRef<any>(null);
  const inFlight = useRef(false);
  const requestSeq = useRef(0);
  const pageLock = useRef(false);

  const [rows, setRows] = useState<AutoTradeCashier[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [appliedSearchTerm, setAppliedSearchTerm] = useState("");
  const [groupIdInput, setGroupIdInput] = useState("");
  const [appliedGroupId, setAppliedGroupId] = useState<number | undefined>();
  const [statusFilter, setStatusFilter] =
    useState<AutoTradeCashierStatusFilter>("all");
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [totalRecords, setTotalRecords] = useState(0);
  const [pendingAction, setPendingAction] = useState<{
    cashier: AutoTradeCashier;
    action: Extract<AutoTradeCashierAction, "suspend" | "unsuspend">;
  } | null>(null);
  const [reason, setReason] = useState("Administrative suspension");
  const [saving, setSaving] = useState(false);
  const [inactiveCashiers, setInactiveCashiers] =
    useState<UpiConfigFlag | null>(null);
  const [inactiveCashiersLoading, setInactiveCashiersLoading] = useState(true);
  const [inactiveCashiersUpdating, setInactiveCashiersUpdating] =
    useState(false);

  useEffect(() => {
    let cancelled = false;

    const loadUpiConfig = async () => {
      try {
        setInactiveCashiersLoading(true);
        const response = await cashierApis.getUpiConfig();
        if (cancelled) return;
        setInactiveCashiers(response.data?.inactiveCashiers ?? "disable");
      } catch (error) {
        if (!cancelled) {
          handleErrorRef.current({
            message: getApiErrorMessage(
              error,
              "Failed to fetch UPI config",
            ),
          });
        }
      } finally {
        if (!cancelled) {
          setInactiveCashiersLoading(false);
        }
      }
    };

    loadUpiConfig();

    return () => {
      cancelled = true;
    };
  }, []);

  const handleInactiveCashiersToggle = async () => {
    if (inactiveCashiers == null || inactiveCashiersUpdating) return;
    const nextStatus: UpiConfigFlag =
      inactiveCashiers === "enable" ? "disable" : "enable";

    setInactiveCashiersUpdating(true);
    try {
      const response = await cashierApis.updateUpiConfig({
        inactiveCashiers: nextStatus,
      });
      setInactiveCashiers(response.data?.inactiveCashiers ?? nextStatus);
      handleSuccess({
        message:
          response.msg ||
          "Inactive cashier assignment updated successfully",
      });
    } catch (err: any) {
      handleError({
        message:
          err?.response?.data?.msg || "Failed to update UPI config",
      });
    } finally {
      setInactiveCashiersUpdating(false);
    }
  };

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setAppliedGroupId(parseGroupId(groupIdInput));
    }, 400);
    return () => window.clearTimeout(timeout);
  }, [groupIdInput]);

  const fetchCashiers = useCallback(
    async (pageNum: number, reset: boolean) => {
      if (inFlight.current && !reset) return;
      const requestId = ++requestSeq.current;
      inFlight.current = true;

      try {
        setLoading(true);
        if (reset) setError("");

        const response = await cashierApis.getAutoTradeCashiers({
          page: pageNum,
          limit: PAGE_LIMIT,
          status: statusFilter,
          search: appliedSearchTerm || undefined,
          groupId: appliedGroupId,
        });

        if (requestId !== requestSeq.current) return;

        if (response.status && response.status !== "success") {
          if (reset) setRows([]);
          setHasMore(false);
          setError(response.msg || "Failed to fetch auto-trade cashiers");
          return;
        }

        const fetched = response.data?.cashiers ?? [];
        const total = response.data?.pagination?.totalRecords;
        setTotalRecords(typeof total === "number" ? total : fetched.length);
        setHasMore(
          typeof total === "number"
            ? (pageNum - 1) * PAGE_LIMIT + fetched.length < total
            : fetched.length === PAGE_LIMIT,
        );

        if (reset) {
          setRows(fetched);
        } else {
          setRows((prev) => {
            const seen = new Set(prev.map((row) => row.autoTrade.id));
            return [
              ...prev,
              ...fetched.filter((row) => !seen.has(row.autoTrade.id)),
            ];
          });
        }
      } catch {
        if (requestId !== requestSeq.current) return;
        if (reset) setRows([]);
        setHasMore(false);
        setError("Failed to fetch auto-trade cashiers");
      } finally {
        if (requestId === requestSeq.current) {
          setLoading(false);
          inFlight.current = false;
        }
      }
    },
    [appliedGroupId, appliedSearchTerm, statusFilter],
  );

  const refreshList = useCallback(() => {
    pageLock.current = false;
    setPage(1);
    setHasMore(true);
    fetchCashiers(1, true);
  }, [fetchCashiers]);

  useEffect(() => {
    refreshList();
  }, [refreshList]);

  useEffect(() => {
    if (page > 1 && hasMore) {
      fetchCashiers(page, false);
    }
  }, [page, hasMore, fetchCashiers]);

  useEffect(() => {
    if (!loading) pageLock.current = false;
  }, [loading]);

  const handleSearch = useCallback(() => {
    const normalizedSearchTerm = searchTerm.trim();
    if (normalizedSearchTerm === appliedSearchTerm) {
      refreshList();
      return;
    }
    setAppliedSearchTerm(normalizedSearchTerm);
  }, [appliedSearchTerm, refreshList, searchTerm]);

  const handleClearSearch = useCallback(() => {
    setSearchTerm("");
    if (appliedSearchTerm === "") {
      refreshList();
      return;
    }
    setAppliedSearchTerm("");
  }, [appliedSearchTerm, refreshList]);

  const openStatusDialog = useCallback((cashier: AutoTradeCashier) => {
    const action: Extract<AutoTradeCashierAction, "suspend" | "unsuspend"> =
      isSuspended(cashier) ? "unsuspend" : "suspend";
    setReason(
      action === "suspend"
        ? "Administrative suspension"
        : "Administrative unsuspension",
    );
    setPendingAction({ cashier, action });
  }, []);

  const confirmStatusChange = async () => {
    if (!pendingAction || saving) return;
    const { cashier, action } = pendingAction;
    setSaving(true);
    try {
      const response = await cashierApis.updateAutoTradeCashierStatus({
        id: cashier.autoTrade.id,
        action,
        reason: reason.trim() || undefined,
      });
      handleSuccess({
        message:
          response.msg ||
          (action === "suspend"
            ? "Auto-trade cashier suspended"
            : "Auto-trade cashier unsuspended"),
      });
      setPendingAction(null);
      refreshList();
    } catch (err: any) {
      handleError({
        message:
          err?.response?.data?.msg ||
          (action === "suspend"
            ? "Failed to suspend cashier"
            : "Failed to unsuspend cashier"),
      });
    } finally {
      setSaving(false);
    }
  };

  const savingId = saving ? pendingAction?.cashier.autoTrade.id : null;

  const columnDefs: ColDef<AutoTradeCashier>[] = useMemo(
    () => [
      tightColumn(56, {
        colId: "id",
        headerName: "ID",
        filter: "agNumberColumnFilter",
        valueGetter: (params) => params.data?.autoTrade?.id ?? null,
      }),
      tightColumn(78, {
        colId: "status",
        headerName: "Status",
        valueGetter: (params) => (params.data ? recordStatus(params.data) : ""),
        cellRenderer: ({ data }: { data: AutoTradeCashier }) => {
          if (!data) return null;
          const status = recordStatus(data);
          return (
            <span
              className={`inline-flex h-[20px] items-center rounded px-1.5 text-[10px] capitalize ${statusClassName(status)}`}
            >
              {status}
            </span>
          );
        },
      }),
      tightColumn(58, {
        colId: "online",
        headerName: "On",
        headerTooltip: "Online",
        valueGetter: (params) => params.data?.user?.onlineStatus ?? "",
        cellRenderer: ({ data }: { data: AutoTradeCashier }) => {
          const online =
            (data?.user?.onlineStatus ?? "").toLowerCase() !== "offline" &&
            Boolean(data?.user?.onlineStatus);
          if (!data?.user?.onlineStatus) return "—";
          return (
            <span
              className={`inline-flex h-[20px] w-[32px] items-center justify-center rounded text-[8px] font-medium ${
                online
                  ? "bg-bg-status-green text-text-status-green"
                  : "bg-bg-status-red text-text-status-red"
              }`}
            >
              {online ? "ON" : "OFF"}
            </span>
          );
        },
      }),
      tightColumn(72, {
        colId: "userId",
        headerName: "UID",
        filter: "agNumberColumnFilter",
        valueGetter: (params) => params.data?.user?.userId ?? null,
        cellRenderer: ({ data }: { data: AutoTradeCashier }) => (
          <div className="flex w-full items-center justify-between gap-0.5">
            <span className="truncate">{textOrDash(data?.user?.userId)}</span>
            {data?.user?.userId != null && (
              <CopyButton
                copyText={String(data.user.userId)}
                copyMessage="User ID copied!"
                buttonSize="14px"
                copyIconSize={10}
                borderRadius="2px"
              />
            )}
          </div>
        ),
      }),
      tightColumn(104, {
        colId: "username",
        headerName: "Username",
        valueGetter: (params) => params.data?.user?.username ?? "",
        tooltipValueGetter: (params) => params.data?.user?.username || "",
      }),
      tightColumn(128, {
        colId: "email",
        headerName: "Email",
        valueGetter: (params) => params.data?.user?.emailId ?? "",
        tooltipValueGetter: (params) => params.data?.user?.emailId || "",
        cellRenderer: ({ data }: { data: AutoTradeCashier }) => (
          <div className="flex w-full items-center justify-between gap-0.5">
            <span className="truncate">{textOrDash(data?.user?.emailId)}</span>
            {data?.user?.emailId && (
              <CopyButton
                copyText={data.user.emailId}
                copyMessage="Email copied!"
                buttonSize="14px"
                copyIconSize={10}
                borderRadius="2px"
              />
            )}
          </div>
        ),
      }),
      tightColumn(108, {
        colId: "whatsapp",
        headerName: "WhatsApp",
        valueGetter: (params) => params.data?.user?.whatsappNumber ?? "",
        valueFormatter: (params) => textOrDash(params.value),
      }),
      tightColumn(88, {
        colId: "account",
        headerName: "Account",
        valueGetter: (params) => (params.data ? accountLabel(params.data) : ""),
      }),
      tightColumn(66, {
        colId: "currency",
        headerName: "CCY",
        headerTooltip: "Currency",
        valueGetter: (params) => params.data?.balances?.currency ?? "",
        valueFormatter: (params) => textOrDash(params.value),
      }),
      tightColumn(72, {
        colId: "wallet",
        headerName: "Wallet",
        filter: "agNumberColumnFilter",
        valueGetter: (params) => params.data?.balances?.walletBalance ?? null,
        valueFormatter: (params) => formatAmount(params.value),
      }),
      tightColumn(72, {
        colId: "escrow",
        headerName: "Escrow",
        filter: "agNumberColumnFilter",
        valueGetter: (params) => params.data?.balances?.escrowBalance ?? null,
        valueFormatter: (params) => formatAmount(params.value),
      }),
      tightColumn(72, {
        colId: "total",
        headerName: "Total",
        filter: "agNumberColumnFilter",
        valueGetter: (params) => params.data?.balances?.totalBalance ?? null,
        valueFormatter: (params) => formatAmount(params.value),
      }),
      tightColumn(112, {
        colId: "upi",
        headerName: "UPI",
        valueGetter: (params) => (params.data ? upiLabel(params.data) : ""),
        tooltipValueGetter: (params) => {
          if (!params.data) return "";
          const upi = upiLabel(params.data);
          return Number(params.data.paymentMethod?.isDeleted) === 1
            ? `${upi} (deleted)`
            : upi;
        },
        cellRenderer: ({ data }: { data: AutoTradeCashier }) => {
          const upi = data ? upiLabel(data) : "—";
          return (
            <div className="flex w-full items-center justify-between gap-0.5">
              <span className="truncate">{upi}</span>
              {Number(data?.paymentMethod?.isDeleted) === 1 && (
                <span className="shrink-0 text-[8px] text-text-status-red">
                  DEL
                </span>
              )}
              {upi !== "—" && (
                <CopyButton
                  copyText={upi}
                  copyMessage="UPI copied!"
                  buttonSize="14px"
                  copyIconSize={10}
                  borderRadius="2px"
                />
              )}
            </div>
          );
        },
      }),
      tightColumn(126, {
        colId: "holder",
        headerName: "Holder",
        headerTooltip: "Account holder",
        valueGetter: (params) =>
          params.data?.paymentMethod?.accountHolderName ?? "",
        valueFormatter: (params) => textOrDash(params.value),
        tooltipValueGetter: (params) =>
          params.data?.paymentMethod?.accountHolderName || "",
      }),
      tightColumn(74, {
        colId: "bank",
        headerName: "App",
        headerTooltip: "Bank / app",
        valueGetter: (params) =>
          params.data?.paymentMethod?.bankOrAppName ?? "",
        valueFormatter: (params) => textOrDash(params.value),
        tooltipValueGetter: (params) =>
          params.data?.paymentMethod?.bankOrAppName || "",
      }),
      tightColumn(102, {
        colId: "paymentGroup",
        headerName: "Pay group",
        headerTooltip: "Payment group",
        valueGetter: (params) =>
          params.data?.paymentMethod?.paymentGroupName ?? "",
        valueFormatter: (params) => textOrDash(params.value),
        tooltipValueGetter: (params) =>
          params.data?.paymentMethod?.paymentGroupName || "",
      }),
      tightColumn(72, {
        colId: "paymentRecordId",
        headerName: "Pay ID",
        headerTooltip: "Payment record",
        filter: "agNumberColumnFilter",
        valueGetter: (params) =>
          params.data?.paymentMethod?.paymentRecordId ?? null,
        valueFormatter: (params) => textOrDash(params.value),
      }),
      tightColumn(112, {
        colId: "group",
        headerName: "Group",
        valueGetter: (params) => (params.data ? groupLabel(params.data) : ""),
        tooltipValueGetter: (params) =>
          params.data ? groupLabel(params.data) : "",
      }),
      tightColumn(84, {
        colId: "ramp",
        headerName: "Ramp",
        headerTooltip: "Ramp access",
        valueGetter: (params) => (params.data ? rampLabel(params.data) : ""),
      }),
      tightColumn(68, {
        colId: "minFiat",
        headerName: "Min",
        headerTooltip: "Min fiat",
        filter: "agNumberColumnFilter",
        valueGetter: (params) =>
          params.data?.accessAndLimits?.minFiatAmount ?? null,
        valueFormatter: (params) => formatAmount(params.value),
      }),
      tightColumn(68, {
        colId: "maxFiat",
        headerName: "Max",
        headerTooltip: "Max fiat",
        filter: "agNumberColumnFilter",
        valueGetter: (params) =>
          params.data?.accessAndLimits?.maxFiatAmount ?? null,
        valueFormatter: (params) => formatAmount(params.value),
      }),
      tightColumn(76, {
        colId: "orderLock",
        headerName: "Lock",
        headerTooltip: "Order lock",
        valueGetter: (params) =>
          Number(params.data?.accessAndLimits?.orderLock) === 1
            ? "Locked"
            : "Open",
      }),
      tightColumn(78, {
        colId: "cashierState",
        headerName: "Cashier",
        valueGetter: (params) =>
          cashierStateLabel(params.data?.accessAndLimits?.cashierState),
      }),
      tightColumn(96, {
        colId: "lastAssigned",
        headerName: "Assigned",
        headerTooltip: "Last assigned",
        valueGetter: (params) => params.data?.autoTrade?.lastAssignedAt ?? "",
        valueFormatter: (params) => dateLabel(params.value),
        tooltipValueGetter: (params) => dateTooltip(params.value),
      }),
      tightColumn(96, {
        colId: "registered",
        headerName: "Registered",
        valueGetter: (params) => params.data?.autoTrade?.registeredAt ?? "",
        valueFormatter: (params) => dateLabel(params.value),
        tooltipValueGetter: (params) => dateTooltip(params.value),
      }),
      tightColumn(96, {
        colId: "updated",
        headerName: "Updated",
        valueGetter: (params) => params.data?.autoTrade?.updatedAt ?? "",
        valueFormatter: (params) => dateLabel(params.value),
        tooltipValueGetter: (params) => dateTooltip(params.value),
      }),
      tightColumn(96, {
        colId: "lastSeen",
        headerName: "Seen",
        headerTooltip: "Last seen",
        valueGetter: (params) => params.data?.user?.lastSeen ?? "",
        valueFormatter: (params) => dateLabel(params.value),
        tooltipValueGetter: (params) => dateTooltip(params.value),
      }),
      tightColumn(148, {
        colId: "action",
        headerName: "Action",
        pinned: "right",
        sortable: false,
        filter: false,
        cellRenderer: ({ data }: { data: AutoTradeCashier }) => {
          if (!data || isDeleted(data)) {
            return <span className="text-[10px] text-text-subtle">—</span>;
          }
          const suspended = isSuspended(data);
          const isSaving = savingId === data.autoTrade.id;
          return (
            <TableActionButton
              variant={suspended ? "success" : "danger"}
              icon={suspended ? "play_circle" : "pause_circle"}
              loading={isSaving}
              onClick={(event) => {
                event.stopPropagation();
                openStatusDialog(data);
              }}
            >
              {suspended ? "Unsuspend" : "Suspend"}
            </TableActionButton>
          );
        },
      }),
    ],
    [openStatusDialog, savingId],
  );

  const onBodyScroll = useCallback(
    (params: any) => {
      if (pageLock.current || inFlight.current || !hasMore || loading) return;
      const displayedRowCount = params.api.getDisplayedRowCount();
      const lastDisplayedRowIndex = params.api.getLastDisplayedRowIndex();
      if (
        lastDisplayedRowIndex >= displayedRowCount - 5 &&
        displayedRowCount > 0
      ) {
        pageLock.current = true;
        setPage((prev) => prev + 1);
      }
    },
    [hasMore, loading],
  );

  const closeDialog = () => {
    if (saving) return;
    setPendingAction(null);
  };

  return (
    <>
      <div className="flex h-full flex-col">
        <div className="sticky top-0 z-10 flex h-[70px] flex-row border-b border-border-subtle bg-bg-page-body pl-[20px]">
          <div className="mr-4 flex w-full flex-row items-center">
            <div className="flex min-w-0 items-center gap-2">
              <button
                type="button"
                onClick={() => navigate("/cashier-management")}
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-bg-button-secondary text-text-base"
                aria-label="Back to cashier management"
              >
                <span className="material-symbols-outlined text-[18px]">
                  arrow_back
                </span>
              </button>
              <div className="min-w-0">
                <p className="truncate text-[14px] font-semibold text-text-base">
                  Auto Trade UPIs Management
                </p>
                <p className="text-[11px] text-text-subtle">
                  {loading && rows.length === 0
                    ? "Loading..."
                    : `${totalRecords} registered`}
                </p>
              </div>
            </div>
            <div className="ml-auto flex shrink-0 items-center">
              {inactiveCashiersLoading ? (
                <span className="whitespace-nowrap text-[11px] text-text-subtle">
                  Loading config...
                </span>
              ) : inactiveCashiers == null ? null : (
                <div className="flex items-center gap-1.5 rounded-md border border-border-subtle bg-bg-page-body px-2 py-1">
                  <span className="whitespace-nowrap text-[11px] font-semibold text-text-subtle">
                    Inactive Cashiers
                  </span>
                  <span
                    className={`text-[10px] font-medium ${
                      inactiveCashiers === "enable"
                        ? "text-text-status-green"
                        : "text-text-status-red"
                    }`}
                  >
                    {inactiveCashiers === "enable" ? "On" : "Off"}
                  </span>
                  <div
                    className={
                      inactiveCashiersUpdating
                        ? "pointer-events-none opacity-50"
                        : ""
                    }
                  >
                    <ToggleInput
                      isToggled={inactiveCashiers === "enable"}
                      onToggle={handleInactiveCashiersToggle}
                      size="sm"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div
          className={`flex min-h-0 flex-1 flex-col overflow-hidden bg-bg-page-body ${
            isMobile ? "mx-3 mt-3" : "mx-4 mt-4"
          }`}
        >
          <div
            className={`mb-4 flex shrink-0 flex-wrap items-center justify-between gap-3 ${
              isMobile ? "flex-col items-stretch" : "flex-row"
            }`}
          >
            <div className="flex items-center justify-start space-x-3">
              <p className="shrink-0 text-[10px] font-bold text-text-base">
                Filter by Status:
              </p>
              <div className="flex space-x-3">
                {STATUS_OPTIONS.map((option) => (
                  <div
                    key={option.value}
                    onClick={
                      loading ? undefined : () => setStatusFilter(option.value)
                    }
                    className={`rounded-[100px] border px-[16px] py-[4px] text-[10px] font-medium text-text-base ${
                      loading
                        ? "cursor-not-allowed opacity-50"
                        : "cursor-pointer"
                    } ${
                      statusFilter === option.value
                        ? "border-border-button-secondary bg-bg-button-secondary"
                        : "border-border-subtle bg-bg-page-body"
                    }`}
                  >
                    {option.label}
                  </div>
                ))}
              </div>
            </div>
            <div
              className={`flex items-center gap-2 ${
                isMobile ? "w-full" : "shrink-0"
              }`}
            >
              <input
                value={groupIdInput}
                onChange={(event) =>
                  setGroupIdInput(event.target.value.replace(/\D/g, ""))
                }
                placeholder="Group ID"
                inputMode="numeric"
                aria-label="Filter by group ID"
                className="h-[38px] w-[110px] shrink-0 rounded-lg border border-border-subtle bg-transparent px-2 text-[10px] text-text-base outline-none placeholder:text-text-header"
              />
              <div className={isMobile ? "min-w-0 flex-1" : "w-[360px]"}>
                <GlobalSearchInputField
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  onSearch={handleSearch}
                  onClear={handleClearSearch}
                  disabled={loading && rows.length === 0}
                  isSearchApplied={Boolean(appliedSearchTerm)}
                  placeholder="Search user, email, WhatsApp, UPI"
                />
              </div>
            </div>
          </div>

          <div className="flex min-h-0 flex-1 flex-col">
            {loading && page === 1 ? (
              <AgGridSkeleton
                columnDefs={columnDefs}
                style={{
                  height: "calc(100vh - 150px)",
                  width: "100%",
                  minHeight: 0,
                }}
              />
            ) : (
              <div className="flex h-full min-h-0 flex-col">
                <div className="ag-theme-alpine ag-grid-12px min-h-0 flex-1">
                  <AgGridReactTable
                    ref={gridRef}
                    rowData={rows}
                    columnDefs={columnDefs}
                    defaultColDef={TIGHT_GRID_DEFAULT}
                    onBodyScroll={onBodyScroll}
                    error={error}
                    getRowId={(params) =>
                      String(
                        params.data?.autoTrade?.id ??
                          params.data?.paymentMethod?.paymentRecordId,
                      )
                    }
                    style={{
                      height: "calc(100vh - 150px)",
                      width: "100%",
                      minHeight: 0,
                    }}
                  />
                </div>
                {loading && rows.length > 0 && (
                  <div className="flex items-center justify-center border-t border-border-subtle py-3 text-[12px] text-text-subtle">
                    Loading more cashiers...
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <SlidingPanel
        isOpen={pendingAction != null}
        onClose={closeDialog}
        onClickCross={closeDialog}
        isMobile={isMobile}
        showConfirm={false}
        heading={
          pendingAction?.action === "unsuspend"
            ? "Unsuspend auto-trade UPI"
            : "Suspend auto-trade UPI"
        }
      >
        {pendingAction && (
          <div className="flex flex-col px-[16px] py-[16px]">
            <p className="text-[12px] text-text-subtle">
              {textOrDash(pendingAction.cashier.user?.username)} ·{" "}
              {upiLabel(pendingAction.cashier)}
            </p>
            <p className="mt-2 text-[12px] text-text-base">
              {pendingAction.action === "suspend"
                ? "An admin suspension stays in place until it is cleared here. The cashier cannot turn it back on."
                : "This clears the admin suspension. A paused registration stays paused."}
            </p>
            <label className="mt-4 block text-[11px] text-text-subtle">
              Reason
              <textarea
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                rows={3}
                className="mt-1 w-full rounded border border-border-subtle bg-bg-page-body p-2 text-[12px] text-text-base outline-none"
              />
            </label>
            <div className="mt-4 flex justify-end gap-2">
              <LightCustomButton
                text="CANCEL"
                isActive={!saving}
                onClick={closeDialog}
              />
              <LightCustomButton
                text={
                  pendingAction.action === "suspend" ? "SUSPEND" : "UNSUSPEND"
                }
                isActive={true}
                isButtonLoading={saving}
                onClick={confirmStatusChange}
              />
            </div>
          </div>
        )}
      </SlidingPanel>
    </>
  );
};

export default AutoTradeUpisManagement;
